<template>
    <ModalShell :title="CONNECT_YOUR_AI" :subtitle="WHAT_IT_DOES">
        <template #icon>
            <SparklesIcon class="connect-ai-glyph" aria-hidden="true" />
        </template>
        <template #header-action>
            <button type="button" class="close-btn" aria-label="Close" @click="close">
                <PhX :size="20" aria-hidden="true" />
            </button>
        </template>

        <div class="connect-ai-body">
            <div class="server-url">
                <span class="field-label">Server URL</span>
                <CopyableValue :value="url" label="Copy the server URL" data-testid="connect-ai-url" />
            </div>

            <PrimeTabs v-model:value="active" :show-navigators="false" class="w-full">
                <!-- Tabs that wrap onto further rows rather than scroll, so every AI stays in sight
                     and there is nothing for scroll arrows to do, and a select on a phone, where the
                     rows would push the steps off the screen. A strip the member has to scroll
                     sideways hides the AI they came for. -->
                <PrimeTabList class="client-tabs" :pt="{ tabList: { 'aria-label': 'Your AI' } }">
                    <PrimeTab v-for="guide in guides" :key="guide.id" :value="guide.id">{{ guide.label }}</PrimeTab>
                </PrimeTabList>
                <PrimeSelect
                    v-model="active"
                    class="client-select"
                    :options="guides"
                    option-label="label"
                    option-value="id"
                    ariaLabel="Your AI"
                />

                <PrimeTabPanels>
                    <PrimeTabPanel v-for="guide in guides" :key="guide.id" :value="guide.id" :data-testid="`connect-ai-steps-${guide.id}`">
                        <ol class="steps">
                            <!-- The column is inside the item: a flex item is no longer a list item,
                                 and loses its number. -->
                            <li v-for="(step, stepIndex) in guide.steps" :key="stepIndex">
                                <div class="step">
                                    <span>
                                        <template v-for="(run, runIndex) in step.text" :key="runIndex">
                                            <template v-if="typeof run === 'string'">{{ run }}</template>
                                            <strong v-else-if="'ui' in run">{{ run.ui }}</strong>
                                            <code v-else class="literal">{{ run.literal }}</code>
                                        </template>
                                    </span>
                                    <PrimeButton
                                        v-if="step.install"
                                        as="a"
                                        :href="step.install.href"
                                        :label="step.install.label"
                                        variant="secondary"
                                        class="self-start"
                                    />
                                    <CopyableValue v-if="step.copy" :value="step.copy.value" :label="step.copy.label" :block="step.copy.block" />
                                </div>
                            </li>
                        </ol>

                        <div class="docs-links">
                            <a v-for="docs in guide.docs" :key="docs.href" :href="docs.href" target="_blank" rel="noopener noreferrer" class="docs-link">
                                {{ docs.label }}
                                <PhArrowSquareOut :size="12" aria-hidden="true" />
                                <span class="sr-only">(opens in a new tab)</span>
                            </a>
                        </div>
                    </PrimeTabPanel>
                </PrimeTabPanels>
            </PrimeTabs>
        </div>
    </ModalShell>
</template>

<script setup lang="ts">
/**
 * Connect your AI: how a member connects an AI agent to their own Privacy Bee account.
 *
 * The steps, the AIs we support and the names the connection carries all come from
 * `pb-ai-connectors`, which the admin console's own guide reads too, so a vendor that moves a
 * menu is corrected once for both. This dialog supplies the one thing the package cannot know:
 * the address of the member server in the environment it runs in.
 */
import { SparklesIcon } from '@heroicons/vue/24/outline';
import { PhArrowSquareOut, PhX } from '@phosphor-icons/vue';
import { connectorGuides, MEMBER_SERVER } from 'pb-ai-connectors';
import PrimeButton from 'primevue/button';
import PrimeSelect from 'primevue/select';
import { ref } from 'vue';
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import CopyableValue from '~/components/shared/CopyableValue.vue';
import { getDialogRef } from '~/composables/dynamic-dialog';
import { ModalNames } from '~/types/modals';

const CONNECT_YOUR_AI = 'Connect your AI';
const WHAT_IT_DOES = 'It lets your AI read your Privacy Bee data and make the changes you approve.';

const dialogRef = getDialogRef(ModalNames.CONNECT_AI);

/**
 * The member server is `/mcp` on the API's own host, the host that is also the OAuth issuer
 * (app-api `config('mcp.resource')`), so it is read off the issuer this app already signs in
 * against and each environment's build dials its own server.
 */
const url = new URL('/mcp', useRuntimeConfig().public.pb.authHost).href;

const guides = connectorGuides(MEMBER_SERVER, url);
const active = ref(guides[0]?.id);

function close() {
    dialogRef.value.close();
}
</script>

<style lang="scss" scoped>
.connect-ai-glyph {
    width: var(--icon-lg);
    height: var(--icon-lg);
}

.close-btn {
    @apply flex h-8 w-8 items-center justify-center rounded-full;
    color: var(--ink-muted);
    transition: background-color var(--duration-fast) var(--ease-standard), color var(--duration-fast) var(--ease-standard);

    &:hover {
        background: var(--surface-sunken);
        color: var(--ink);
    }
}

.connect-ai-body {
    @apply flex flex-col w-full;
    gap: var(--space-4);
    padding-bottom: var(--space-6);
}

.server-url {
    @apply flex flex-col;
    gap: var(--space-1);
}

.field-label {
    color: var(--ink-body);
    font-size: var(--text-sm);
    font-weight: var(--weight-medium);
    line-height: var(--leading-sm);
}

.client-tabs {
    @apply hidden sm:flex;
    width: auto;
    margin-bottom: var(--space-4);

    :deep([data-pc-section='tablist']) {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-1);
    }
}

.client-select {
    @apply flex w-full sm:hidden;
    margin-bottom: var(--space-4);
}

// A block list rather than a flex column, so each item keeps its number; the space between them
// is each item's margin.
.steps {
    @apply list-decimal m-0;
    padding-left: var(--space-5);
    color: var(--ink-body);
    font-size: var(--text-sm);
    line-height: var(--leading-sm);
}

.steps > li + li {
    margin-top: var(--space-3);
}

.step {
    @apply flex flex-col;
    gap: var(--space-2);

    strong {
        color: var(--ink);
        font-weight: var(--weight-semibold);
    }
}

.literal {
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    overflow-wrap: anywhere;
}

.docs-links {
    @apply flex flex-col items-start;
    gap: var(--space-2);
    margin-top: var(--space-4);
}

.docs-link {
    @apply inline-flex items-center no-underline hover:underline;
    gap: var(--space-1);
    color: var(--action-ink);
    font-size: var(--text-sm);
    font-weight: var(--weight-medium);
}
</style>
